(() => {
  'use strict';

  if (window.rudcaQuantityControlsReady) return;
  window.rudcaQuantityControlsReady = true;

  const quantitySelector = '[data-rudca-quantity]';

  const getField = (quantity) => quantity.closest('form')?.querySelector('[data-rudca-quantity-value]');
  const getRules = (quantity) => ({
    min: Math.max(1, Number(quantity.dataset.min) || 1),
    step: Math.max(1, Number(quantity.dataset.step) || 1),
    max: quantity.hasAttribute('data-max') ? Number(quantity.dataset.max) : Number.MAX_SAFE_INTEGER
  });

  // Presentation only. The cart writer, prices, limits and checkout use their own live state.
  const headerCount = (() => {
    let target = null, quantities = null, scheduled = false, generation = 0, sequence = 0;
    const holds = new Map(), blocked = new Set();
    const valid = value => Number.isSafeInteger(value) && value >= 0;
    const paint = () => {
      scheduled = false;
      const held = [...holds.values()].reduce((sum, token) => sum + token.remaining, 0);
      const value = target === null ? '…' : String(Math.max(0, target - held));
      for (const node of document.querySelectorAll('.header__cart-count')) if (node.textContent !== value) node.textContent = value;
    };
    const schedule = () => { if (!scheduled) { scheduled = true; Promise.resolve().then(paint); } };
    const finish = (token, reconciled = false) => {
      if (!token || token.generation !== generation || !holds.has(token.id) || token.rejected && !reconciled) return;
      clearTimeout(token.timer); holds.delete(token.id); token.remaining = 0; schedule();
    };
    const clear = () => { for (const token of holds.values()) clearTimeout(token.timer); holds.clear(); blocked.clear(); generation += 1; };
    const project = (count, next) => {
      if (!valid(count)) { clear(); target = null; quantities = null; schedule(); stopCartFeedback(); return; }
      if (next && quantities) {
        for (const [identity, previous] of Object.entries(quantities)) {
          let removed = Math.max(0, previous - (next[identity] || 0));
          if (!removed) continue;
          // A removed unit that has not arrived must never later reappear in the badge.
          for (const token of holds.values()) {
            if (token.identity !== identity || !removed) continue;
            const amount = Math.min(removed, token.remaining);
            token.remaining -= amount; removed -= amount;
            if (!token.remaining) finish(token, true);
          }
        }
      }
      target = count; quantities = next || null; schedule();
    };
    const reserve = detail => {
      if (!Number.isSafeInteger(detail?.delta) || detail.delta <= 0 || typeof detail.identity !== 'string' || !detail.identity) return null;
      const token = { id: ++sequence, generation, identity: detail.identity, remaining: detail.delta, rejected: blocked.has(detail.identity), claimed: false, started: 0, timer: null };
      holds.set(token.id, token); schedule(); return token;
    };
    const claim = token => {
      if (!token || !holds.has(token.id) || token.generation !== generation) return;
      token.claimed = true; token.started = Date.now();
      token.timer = setTimeout(() => finish(token), 1800);
    };
    const evicted = token => {
      if (!token || !holds.has(token.id)) return;
      clearTimeout(token.timer);
      token.timer = setTimeout(() => finish(token), Math.max(0, token.started + 1400 - Date.now()));
    };
    const reject = identity => { if (identity) blocked.add(identity); for (const token of holds.values()) if (token.identity === identity) { token.rejected = true; clearTimeout(token.timer); } };
    const reconcile = () => { blocked.clear(); for (const token of holds.values()) if (token.rejected) finish(token, true); };
    const skipMotion = () => { for (const token of holds.values()) finish(token); };
    const reset = () => { clear(); schedule(); };
    const initial = document.querySelector('.header__cart-count')?.textContent.trim();
    if (/^\d+$/.test(initial || '')) target = Number(initial);
    window.addEventListener('pagehide', reset);
    window.addEventListener('pageshow', event => { if (event.persisted) reset(); });
    return Object.freeze({ project, reserve, claim, finish, evicted, reject, reconcile, skipMotion, reset });
  })();
  window.RudcaHeaderCount = headerCount;

  let activeCartFlight = null;
  let activeCartArrival = null;
  const activeCartFlights = new Set();
  const maxCartFlights = 4;

  function stopCartArrival() {
    activeCartArrival?.cleanup();
  }

  function animateCartArrival(cartRowKey = null, photo = false) {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    // The count shares this wrapper: spring only the current SVG, never the badge.
    const icon = document.querySelector('.header__cart-toggle .icon-state__primary svg');
    if (!icon || icon.closest('.icon-state[aria-expanded="true"]')) return;
    const bounds = icon.getBoundingClientRect();
    const viewport = window.visualViewport;
    const left = viewport?.offsetLeft || 0;
    const top = viewport?.offsetTop || 0;
    const width = viewport?.width || document.documentElement.clientWidth || window.innerWidth;
    const height = viewport?.height || document.documentElement.clientHeight || window.innerHeight;
    const style = window.getComputedStyle(icon);
    if (bounds.width <= 0 || bounds.height <= 0 || bounds.right <= left || bounds.left >= left + width ||
      bounds.bottom <= top || bounds.top >= top + height || style.visibility === 'hidden' ||
      style.display === 'none' || style.opacity === '0') return;

    stopCartArrival();
    const originalStyles = ['transform-origin', 'transform-box'].map((property) => ({
      property, value: icon.style.getPropertyValue(property), priority: icon.style.getPropertyPriority(property)
    }));
    const arrival = { animation: null, timeout: null, done: false, cleanup: null, cartRowKey, photo };
    arrival.cleanup = () => {
      if (arrival.done) return;
      arrival.done = true;
      window.clearTimeout(arrival.timeout);
      arrival.animation?.cancel();
      for (const { property, value, priority } of originalStyles) {
        if (value) icon.style.setProperty(property, value, priority);
        else icon.style.removeProperty(property);
      }
      if (activeCartArrival === arrival) activeCartArrival = null;
    };
    activeCartArrival = arrival;
    icon.style.setProperty('transform-origin', '50% 50%');
    icon.style.setProperty('transform-box', 'fill-box');
    try {
      arrival.animation = icon.animate([
        { transform: 'translateY(0) scale(1)', opacity: 1, offset: 0 },
        { transform: 'translateY(1px) scale(0.92)', opacity: 0.9, offset: 0.3 },
        { transform: 'translateY(-1px) scale(1.06)', opacity: 1, offset: 0.65 },
        { transform: 'translateY(0) scale(1)', opacity: 1, offset: 1 }
      ], { duration: 280, easing: 'ease-out' });
      arrival.timeout = window.setTimeout(arrival.cleanup, 600);
      arrival.animation.finished.then(arrival.cleanup, arrival.cleanup);
    } catch (_) {
      arrival.cleanup();
    }
  }

  function stopCartFlight() {
    activeCartFlight?.cleanup();
  }

  function stopCartFeedback() {
    for (const flight of activeCartFlights) flight.cleanup();
    stopCartArrival();
  }

  function rejectCartRowFeedback(event) {
    const key = event.detail?.key;
    const all = event.detail?.all === true;
    if (!all && !key) return;
    for (const flight of activeCartFlights) {
      if (flight.cartRowKey && (all || flight.cartRowKey === key)) flight.cleanup();
    }
    if (activeCartArrival?.cartRowKey && (all || activeCartArrival.cartRowKey === key)) stopCartArrival();
  }

  function animatePhotoIntent(event) {
    const button = event.detail?.button;
    const control = button instanceof Element ? button.closest('[data-photo-control]') : null;
    if (!control) return false;
    const picker = control?.closest('rudca-photo-scroll[data-photo-theme="188331491554"][data-photo-global]');
    if (!picker) return true;
    // The shared cart writer accepted this intent; movement is input feedback, not confirmation.
    // Every visible photo owns its control; the center selection must never retarget a tap.
    const item = control.closest('[data-photo-item]');
    const rail = picker.rail;
    const product = control.dataset.photoControl;
    if (picker.locked || picker.pointer || picker.pages?.failed || Date.now() < picker.suppressUntil ||
      !rail || rail.hasAttribute('data-dragging') || !item || !rail.contains(item) ||
      item.closest('rudca-photo-scroll') !== picker || item.dataset.photoItem !== product ||
      !/^[1-9]\d*$/.test(product || '') || control.dataset.rudcaProductId !== product ||
      control.hasAttribute('data-photo-control-loading') || !picker.controlCards?.includes(control) ||
      item.querySelectorAll('[data-photo-control]').length !== 1) return true;
    const form = button.closest('[data-rudca-basket-card]');
    const detail = event.detail;
    if (!form || form !== detail.form || form.closest('[data-photo-control]') !== control ||
      control.querySelectorAll('form').length !== 1 || form.closest('[hidden], [inert]') || !form.isConnected ||
      !button.matches(detail.initial ? '[data-action="add-to-cart"], [data-rudca-product-add], [data-rudca-grid-add]' : '[data-rudca-basket-change]') ||
      !Number.isSafeInteger(detail.confirmed) || detail.confirmed < 0 || !Number.isSafeInteger(detail.desired) || detail.desired < 0 ||
      !Number.isSafeInteger(detail.delta) || detail.delta === 0) return true;
    const fields = new FormData(form), variants = fields.getAll('id');
    const rules = form.querySelector('[data-rudca-basket-selected][data-variant-id]');
    if (variants.length !== 1 || String(variants[0]) !== String(detail.variant) || !/^[1-9]\d*$/.test(String(detail.variant)) ||
      !rules || rules.dataset.variantId !== String(detail.variant) ||
      fields.getAll('selling_plan').some(value => String(value) !== '') || [...fields].some(([name, value]) => /^properties\[/.test(name) && String(value) !== '')) return true;
    const links = [...item.querySelectorAll('[data-photo-link][data-photo-product]')];
    const link = links.length === 1 && links[0];
    const source = link && link.dataset.photoProduct === product && picker.links?.includes(link) && link.querySelector('img');
    if (!source || source.closest('[data-photo-item]') !== item || !source.complete || !source.naturalWidth) return true;
    const bounds = source.getBoundingClientRect();
    const viewport = window.visualViewport;
    const left = viewport?.offsetLeft || 0, top = viewport?.offsetTop || 0;
    const width = viewport?.width || window.innerWidth, height = viewport?.height || window.innerHeight;
    const railBounds = rail.getBoundingClientRect();
    const visible = (node, rect = node.getBoundingClientRect()) => {
      if (rect.width <= 0 || rect.height <= 0 || railBounds.width <= 0 || railBounds.height <= 0 ||
        Math.min(rect.right, railBounds.right, left + width) <= Math.max(rect.left, railBounds.left, left) ||
        Math.min(rect.bottom, railBounds.bottom, top + height) <= Math.max(rect.top, railBounds.top, top) ||
        node.closest('[hidden], [inert]')) return false;
      for (let ancestor = node; ancestor; ancestor = ancestor.parentElement) {
        const style = window.getComputedStyle(ancestor);
        if (style.visibility === 'hidden' || style.visibility === 'collapse' || style.display === 'none' ||
          style.opacity === '0' || style.contentVisibility === 'hidden') return false;
      }
      return true;
    };
    const url = source.currentSrc || source.getAttribute('src');
    if (!url || !visible(source, bounds) || !visible(form)) return true;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return true;
    const anchor = Object.freeze({ picker, rail, item, link, source, control, form, rules,
      field: form.querySelector('[name="id"]'), src: source.getAttribute('src'),
      srcset: source.getAttribute('srcset'), photoSrc: source.getAttribute('data-photo-src') });
    const snapshot = Object.freeze({ product, variant: String(detail.variant), url, anchor,
      bounds: Object.freeze({ left: bounds.left, top: bounds.top, right: bounds.right, bottom: bounds.bottom, width: bounds.width, height: bounds.height }) });
    animateProductToCart({ type: 'rudca:photo-basket-intent', detail: { button, delta: detail.delta, photoSnapshot: snapshot, countToken: detail.countToken, identity: detail.identity } });
    return true;
  }
  function stopPhotoFeedback(event) {
    const identity = event?.detail?.identity;
    const all = event?.detail?.all === true;
    if (identity) headerCount.reject(identity);
    for (const flight of activeCartFlights) if (all || identity && flight.identity === identity) flight.cleanup();
    if (activeCartArrival?.photo && all) stopCartArrival();
  }

  function photoReturnBounds(snapshot) {
    const { picker, rail, item, link, source, control, form, rules, field, src, srcset, photoSrc } = snapshot.anchor;
    // Keep the accepted product/variant AND exact DOM occurrence. Never look up
    // the center, a replacement card, or another occurrence of the same product.
    if (!picker.isConnected || picker.rail !== rail || !rail.contains(item) ||
      item.closest('rudca-photo-scroll') !== picker || item.dataset.photoItem !== snapshot.product ||
      !picker.links?.includes(link) || !picker.controlCards?.includes(control) ||
      link.closest('[data-photo-item]') !== item || link.dataset.photoProduct !== snapshot.product ||
      source !== link.querySelector('img') || !source.complete || !source.naturalWidth ||
      source.getAttribute('src') !== src || source.getAttribute('srcset') !== srcset ||
      source.getAttribute('data-photo-src') !== photoSrc || (source.currentSrc || source.getAttribute('src')) !== snapshot.url ||
      control.closest('[data-photo-item]') !== item || control.dataset.photoControl !== snapshot.product ||
      control.dataset.rudcaProductId !== snapshot.product || form !== control.querySelector('form') ||
      rules !== form.querySelector('[data-rudca-basket-selected][data-variant-id]') || rules.dataset.variantId !== snapshot.variant ||
      !field || field !== form.querySelector('[name="id"]') || String(field.value) !== snapshot.variant ||
      source.closest('[hidden], [inert]')) return null;
    // Controls become inert during a swipe. That is expected and must not cancel
    // a return to an otherwise visible, unchanged image.
    const bounds = source.getBoundingClientRect(), railBounds = rail.getBoundingClientRect();
    const viewport = window.visualViewport;
    const left = viewport?.offsetLeft || 0, top = viewport?.offsetTop || 0;
    const width = viewport?.width || window.innerWidth, height = viewport?.height || window.innerHeight;
    const clipLeft = Math.max(railBounds.left, left), clipRight = Math.min(railBounds.right, left + width);
    const clipTop = Math.max(railBounds.top, top), clipBottom = Math.min(railBounds.bottom, top + height);
    const centerX = bounds.left + bounds.width / 2, centerY = bounds.top + bounds.height / 2;
    if (bounds.width <= 0 || bounds.height <= 0 || railBounds.width <= 0 || railBounds.height <= 0 ||
      centerX <= clipLeft || centerX >= clipRight || centerY <= clipTop || centerY >= clipBottom ||
      (Math.min(bounds.right, clipRight) - Math.max(bounds.left, clipLeft)) *
        (Math.min(bounds.bottom, clipBottom) - Math.max(bounds.top, clipTop)) < bounds.width * bounds.height * 0.5) return null;
    for (let node = source; node; node = node.parentElement) {
      const style = window.getComputedStyle(node);
      if (style.visibility === 'hidden' || style.visibility === 'collapse' || style.display === 'none' ||
        style.opacity === '0' || style.contentVisibility === 'hidden') return null;
    }
    return bounds;
  }

  function animateProductToCart(event) {
    const detail = event.detail || {};
    const token = detail.countToken || headerCount.reserve(detail);
    const forwarded = { type: event.type, detail: { ...detail, countToken: token } };
    try { animateProductToCartVisual(forwarded); }
    finally { if (token && !token.claimed) headerCount.finish(token); }
  }
  function animateProductToCartVisual(event) {
    if (event.type === 'rudca:basket-intent' && animatePhotoIntent(event)) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      stopCartFeedback();
      return;
    }
    const button = event.detail?.button;
    const basketIntent = event.type === 'rudca:basket-intent';
    const cartRowIntent = event.type === 'rudca:cart-row-intent';
    const photoSnapshot = event.type === 'rudca:photo-basket-intent' ? event.detail?.photoSnapshot : null;
    const legacyIntent = event.type === 'rudca:cart-add-intent';
    const quantityIntent = basketIntent || cartRowIntent || legacyIntent || Boolean(photoSnapshot);
    const delta = Number(event.detail?.delta);
    const cartRow = cartRowIntent && button instanceof Element ? button.closest('[data-rudca-line-key]') : null;
    if (cartRowIntent && (!cartRow || cartRow !== event.detail?.row || !cartRow.isConnected || cartRow.hidden ||
      cartRow.getAttribute('data-rudca-line-key') !== event.detail?.key || !cartRow.closest('[data-section-type="cart"]') ||
      cartRow.closest('.mini-cart, .rudca-cart-complements, [data-rudca-cart-complement]') ||
      !button.matches('button[data-action="increase-quantity"], button[data-action="decrease-quantity"]') ||
      !button.closest('.quantity-selector') || !Number.isSafeInteger(delta) || delta === 0)) return;
    const basketForm = button instanceof Element ? button.closest(legacyIntent ? 'form' : '[data-rudca-basket-card]') : null;
    if (basketIntent && (!basketForm || basketForm.closest('.rudca-cart-complements, [data-rudca-cart-complement]') ||
      !button.matches(event.detail?.initial ? '[data-action="add-to-cart"], [data-rudca-product-add], [data-rudca-grid-add]' : '[data-rudca-basket-change]') ||
      !Number.isSafeInteger(delta) || delta === 0)) return;
    if (basketIntent && basketForm.closest('.rudca-cart-recommendations, [data-rudca-cart-recommendations], [data-rudca-cart-recommendation]')) {
      const root = basketForm.closest('[data-rudca-cart-recommendations]');
      const item = basketForm.closest('[data-rudca-cart-recommendation]');
      if (!root || !item || !root.contains(item) || !root.closest('[data-section-type="cart"]') || basketForm.closest('.mini-cart')) return;
    }
    const reverse = quantityIntent && delta < 0;
    const card = button instanceof Element ? button.closest('.product-item') : null;
    const productSection = button instanceof Element && (basketIntent || legacyIntent || button.hasAttribute('data-rudca-product-add'))
      ? button.closest('[data-section-type="product"], [data-section-type="featured-product"]') : null;
    const source = cartRow?.querySelector('.line-item__image-wrapper img') ||
      card?.querySelector('.product-item__primary-image, .product-item__image-wrapper img') ||
      productSection?.querySelector('.product-gallery__carousel-item.is-selected[data-media-type="image"] .product-gallery__image');
    const cartToggle = document.querySelector('.header__cart-toggle');
    const destination = cartToggle?.querySelector('.icon-state__primary svg') || cartToggle?.querySelector('.header__cart-icon') || cartToggle;
    if ((!source && !photoSnapshot) || !destination || typeof Element.prototype.animate !== 'function') return;
    const sourceUrl = photoSnapshot ? photoSnapshot.url : source.currentSrc || source.getAttribute('src');
    const sourceBounds = photoSnapshot ? photoSnapshot.bounds : source.getBoundingClientRect();
    const targetBounds = destination.getBoundingClientRect();
    if (!sourceUrl || sourceBounds.width <= 0 || sourceBounds.height <= 0 || targetBounds.width <= 0 || targetBounds.height <= 0) return;

    const visualViewport = window.visualViewport;
    const viewport = {
      left: visualViewport?.offsetLeft || 0,
      top: visualViewport?.offsetTop || 0,
      width: visualViewport?.width || document.documentElement.clientWidth || window.innerWidth,
      height: visualViewport?.height || document.documentElement.clientHeight || window.innerHeight
    };
    const visible = (element, bounds) => {
      const style = window.getComputedStyle(element);
      return bounds.width > 0 && bounds.height > 0 && !element.closest('[hidden]') &&
        bounds.right > viewport.left && bounds.left < viewport.left + viewport.width &&
        bounds.bottom > viewport.top && bounds.top < viewport.top + viewport.height &&
        style.visibility !== 'hidden' && style.display !== 'none' && style.opacity !== '0';
    };
    const sourceIsVisible = sourceBounds.right > viewport.left && sourceBounds.left < viewport.left + viewport.width &&
      sourceBounds.bottom > viewport.top && sourceBounds.top < viewport.top + viewport.height;
    let launchBounds = sourceIsVisible ? sourceBounds : button.getBoundingClientRect();
    // Basket motion is a short-lived cue for accepted intent, never a cart confirmation.
    // A mobile gallery may be above the viewport: use its image at this product's live controls.
    if (quantityIntent) {
      if (!visible(destination, targetBounds) || destination.closest('.icon-state[aria-expanded="true"]')) return;
      if (sourceIsVisible) {
        if (!photoSnapshot && !visible(source, sourceBounds)) return;
      } else {
        const candidates = cartRowIntent ? [button, ...cartRow.querySelectorAll('.quantity-selector')] :
          [button, basketForm.querySelector('.rudca-basket-card__quantity'),
          basketForm.querySelector('.rudca-basket-card__resume'),
          basketForm.querySelector('[data-rudca-product-add], [data-rudca-grid-add], [data-action="add-to-cart"]'),
          basketForm.querySelector('.rudca-product-purchase__cart-row')];
        const control = candidates.map((element) => element && ({ element, bounds: element.getBoundingClientRect() }))
          .find((candidate) => candidate && visible(candidate.element, candidate.bounds));
        if (!control) return;
        launchBounds = control.bounds;
      }
    }
    if (!quantityIntent) stopCartFlight();
    stopCartArrival();
    while (activeCartFlights.size >= maxCartFlights) { const oldest = activeCartFlights.values().next().value; headerCount.evicted(oldest.countToken); oldest.cleanup(); }
    const layer = document.createElement('div');
    layer.className = 'rudca-cart-flight';
    layer.setAttribute('aria-hidden', 'true');
    layer.setAttribute('data-rudca-flight-direction', reverse ? 'from-cart' : 'to-cart');
    Object.assign(layer.style, {
      position: 'fixed', inset: '0', overflow: 'hidden', pointerEvents: 'none',
      zIndex: '2147482000', contain: 'strict'
    });
    const image = document.createElement('img');
    image.src = sourceUrl;
    image.alt = '';
    image.draggable = false;
    const margin = 12;
    const ratio = Math.min(1, 150 / Math.max(sourceBounds.width, sourceBounds.height),
      Math.max(1, viewport.width - margin * 2) / sourceBounds.width,
      Math.max(1, viewport.height - margin * 2) / sourceBounds.height);
    const width = sourceBounds.width * ratio;
    const height = sourceBounds.height * ratio;
    const startCenterX = launchBounds.left + launchBounds.width / 2;
    const startCenterY = launchBounds.top + launchBounds.height / 2;
    const endCenterX = Math.min(viewport.left + viewport.width - margin, Math.max(viewport.left + margin, targetBounds.left + targetBounds.width / 2));
    const endCenterY = Math.min(viewport.top + viewport.height - margin, Math.max(viewport.top + margin, targetBounds.top + targetBounds.height / 2));
    const place = (centerX, centerY, scale) => {
      const x = Math.min(viewport.left + viewport.width - margin - width * scale / 2,
        Math.max(viewport.left + margin + width * scale / 2, centerX));
      const y = Math.min(viewport.top + viewport.height - margin - height * scale / 2,
        Math.max(viewport.top + margin + height * scale / 2, centerY));
      return `translate(${x - width / 2}px, ${y - height / 2}px) scale(${scale})`;
    };
    Object.assign(image.style, {
      position: 'absolute', left: '0', top: '0', width: `${width}px`, height: `${height}px`,
      margin: '0', padding: '0', border: '0', objectFit: 'contain', pointerEvents: 'none',
      transformOrigin: 'center', willChange: 'transform, opacity'
    });
    let movingNode = image;
    if (photoSnapshot) {
      // Each accepted tap moves immediately; the shared active-flight cap bounds rapid taps.
      movingNode = document.createElement('div');
      Object.assign(movingNode.style, { position: 'absolute', left: '0', top: '0', width: `${width}px`, height: `${height}px`, transformOrigin: 'center', willChange: 'transform, opacity' });
      const amount = document.createElement('span');
      amount.textContent = `${delta > 0 ? '+' : '−'}${Math.abs(delta)}`;
      amount.className = 'rudca-photo-flight__quantity';
      Object.assign(amount.style, { position: 'absolute', right: '0', top: '0', minWidth: '28px', padding: '3px 7px', borderRadius: '20px', background: '#fff', color: '#222', boxShadow: '0 1px 6px #0003', font: '600 14px/20px system-ui', textAlign: 'center' });
      movingNode.append(image, amount);
      layer.setAttribute('data-rudca-flight-quantity', String(delta));
      layer.setAttribute('data-rudca-flight-product', photoSnapshot.product);
      layer.setAttribute('data-rudca-flight-variant', photoSnapshot.variant);
    }
    layer.appendChild(movingNode);
    document.body.appendChild(layer);
    const flight = { identity: event.detail?.identity, countToken: event.detail?.countToken, layer, animation: null, fade: null, frame: null, observer: null, timeout: null, done: false, cleanup: null, cartRowKey: cartRowIntent ? event.detail.key : null, photo: Boolean(photoSnapshot) };
    activeCartFlights.add(flight);
    headerCount.claim(flight.countToken);
    if (!quantityIntent) activeCartFlight = flight;
    const cleanup = () => {
      if (flight.done) return;
      flight.done = true;
      window.clearTimeout(flight.timeout);
      window.cancelAnimationFrame(flight.frame);
      flight.observer?.disconnect();
      flight.fade?.cancel();
      flight.animation?.cancel();
      layer.remove();
      activeCartFlights.delete(flight);
      if (activeCartFlight === flight) activeCartFlight = null;
    };
    flight.cleanup = cleanup;
    try {
      const forwardFrames = [
        { transform: place(startCenterX, startCenterY, 1), opacity: 0.8, offset: 0 },
        { transform: place((startCenterX + endCenterX) / 2, Math.min(startCenterY, endCenterY) - 32, 0.65), opacity: 0.7, offset: 0.6 },
        { transform: place(endCenterX, endCenterY, 0.28), opacity: 0.6, offset: 0.85 },
        { transform: place(endCenterX, endCenterY, 0.04), opacity: 0, offset: 1 }
      ];
      const reverseFrames = [
        { transform: place(endCenterX, endCenterY, 0.12), opacity: 0.6, offset: 0 },
        { transform: place((startCenterX + endCenterX) / 2, Math.min(startCenterY, endCenterY) - 32, 0.65), opacity: 0.7, offset: 0.6 },
        { transform: place(startCenterX, startCenterY, 1), opacity: 0.65, offset: 0.88 },
        { transform: place(startCenterX, startCenterY, 1), opacity: 0, offset: 1 }
      ];
      flight.animation = movingNode.animate(reverse ? reverseFrames : forwardFrames,
        { duration: 1400, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)', fill: 'forwards' });
      flight.timeout = window.setTimeout(cleanup, 1800);
      flight.animation.finished.then(() => {
        const arrived = !flight.done && !reverse;
        if (arrived) headerCount.finish(flight.countToken);
        cleanup();
        if (arrived) animateCartArrival(flight.cartRowKey, flight.photo);
      }, cleanup);
      if (reverse && photoSnapshot) {
        // Keep the ordinary 1400ms clock and easing. Only the exact return
        // endpoint changes as its image translates/scales with native scrolling.
        const framesFor = (bounds) => {
          const x = bounds.left + bounds.width / 2, y = bounds.top + bounds.height / 2;
          const scale = Math.min(bounds.width / sourceBounds.width, bounds.height / sourceBounds.height);
          const endpoint = `translate(${x - width / 2}px, ${y - height / 2}px) scale(${scale})`;
          return [reverseFrames[0],
            { transform: place((x + endCenterX) / 2, Math.min(y, endCenterY) - 32, 0.65), opacity: 0.7, offset: 0.6 },
            { transform: endpoint, opacity: 0.65, offset: 0.88 },
            { transform: endpoint, opacity: 0, offset: 1 }];
        };
        const fadeLostTarget = () => {
          if (flight.done || flight.fade) return;
          window.cancelAnimationFrame(flight.frame);
          flight.observer?.disconnect();
          // Pause where the cue actually is; never finish at an obsolete point
          // or resurrect it if the old product becomes visible again later.
          try {
            flight.animation.pause();
            flight.fade = layer.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 120, easing: 'ease-out', fill: 'forwards' });
            flight.fade.finished.then(cleanup, cleanup);
          } catch (_) { cleanup(); }
        };
        const { rail, item, link, source: photo, control, form } = photoSnapshot.anchor;
        flight.observer = new MutationObserver((records) => {
          // A compact-cycle rotation can remove and reinsert the same node in
          // one task. That is a new occurrence even when its URL is unchanged.
          if (records.some(record => [...record.removedNodes].some(node =>
            [item, link, photo, control, form].some(anchor => node === anchor || node.contains?.(anchor))))) fadeLostTarget();
        });
        flight.observer.observe(rail, { childList: true, subtree: true });
        let lastBounds = null;
        const follow = () => {
          if (flight.done || flight.fade) return;
          const bounds = photoReturnBounds(photoSnapshot);
          if (!bounds) { fadeLostTarget(); return; }
          if (!lastBounds || ['left', 'top', 'width', 'height'].some(key => bounds[key] !== lastBounds[key])) {
            try { flight.animation.effect.setKeyframes(framesFor(bounds)); }
            catch (_) { fadeLostTarget(); return; }
            lastBounds = { left: bounds.left, top: bounds.top, width: bounds.width, height: bounds.height };
          }
          flight.frame = window.requestAnimationFrame(follow);
        };
        follow();
      }
    } catch (_) {
      headerCount.finish(flight.countToken);
      cleanup();
    }
  }

  function validate(quantity, rawValue) {
    const text = String(rawValue).trim();
    const value = Number(text);
    const rules = getRules(quantity);
    if (!/^[0-9]+$/.test(text) || !Number.isSafeInteger(value) || value < 1) {
      return quantity.dataset.errorInteger;
    }
    if (value < rules.min) return quantity.dataset.errorMinimum;
    if (value > rules.max) return quantity.dataset.errorMaximum;
    if ((value - rules.min) % rules.step !== 0) return quantity.dataset.errorIncrement;
    return '';
  }

  function showError(element, message) {
    if (!element) return;
    element.textContent = message;
    element.hidden = !message;
  }

  function syncQuantity(quantity) {
    const field = getField(quantity);
    if (!field) return;
    const rules = getRules(quantity);
    const value = Number(field.value);
    const invalid = Boolean(validate(quantity, field.value));
    const output = quantity.querySelector('[data-rudca-quantity-count]');
    if (output) output.value = field.value;
    const minus = quantity.querySelector('[data-rudca-quantity-minus]');
    const plus = quantity.querySelector('[data-rudca-quantity-plus]');
    if (minus) {
      minus.hidden = false;
      minus.disabled = invalid || value - rules.step < rules.min;
    }
    if (plus) {
      plus.hidden = false;
      plus.disabled = invalid || value + rules.step > rules.max || !Number.isSafeInteger(value + rules.step);
    }

  }

  function updateQuantity(quantity, value) {
    const field = getField(quantity);
    if (!field) return;
    field.value = String(value);
    syncQuantity(quantity);
    showError(quantity.querySelector('[data-rudca-quantity-error]'), '');
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function initialize(root = document) {
    if (root.matches?.(quantitySelector)) syncQuantity(root);
    root.querySelectorAll?.(quantitySelector).forEach(syncQuantity);
  }

  function validateAdd(form, event) {
    const quantity = form?.querySelector(quantitySelector);
    if (!quantity) return;
    const field = getField(quantity);
    if (!field) return;
    const message = validate(quantity, field.value);
    showError(quantity.querySelector('[data-rudca-quantity-error]'), message);
    if (!message) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!target) return;
    const addButton = target.closest('[data-rudca-grid-add]');
    if (addButton) {
      validateAdd(addButton.form, event);
      return;
    }
    const control = target.closest('[data-rudca-quantity-minus], [data-rudca-quantity-plus]');
    if (control) {
      const quantity = control.closest(quantitySelector);
      if (!quantity) return;
      event.preventDefault();
      event.stopPropagation();
      if (control.disabled) return;
      const field = getField(quantity);
      if (!field) return;
      const step = getRules(quantity).step;
      const value = Number(field.value) + (control.hasAttribute('data-rudca-quantity-plus') ? step : -step);
      const message = validate(quantity, value);
      if (message) {
        showError(quantity.querySelector('[data-rudca-quantity-error]'), message);
        return;
      }
      updateQuantity(quantity, value);
      return;
    }
  }, true);

  document.addEventListener('submit', (event) => {
    if (event.target instanceof HTMLFormElement) validateAdd(event.target, event);
  }, true);

  const start = () => {
    initialize();
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) initialize(node);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  };

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('product:added', () => initialize());
  document.addEventListener('rudca:cart-add-intent', animateProductToCart);
  document.addEventListener('rudca:basket-intent', animateProductToCart);
  document.addEventListener('rudca:cart-row-intent', animateProductToCart);
  document.addEventListener('rudca:cart-row-rejected', rejectCartRowFeedback);
  // Rejection stops outstanding picker motion. Settlement never launches another flight.
  document.addEventListener('rudca:cart-row-rejected', stopPhotoFeedback);
  document.addEventListener('rudca:cart-count-rejected', stopPhotoFeedback);
  document.addEventListener('rudca:cart-settled', (event) => {
    headerCount.reconcile();
  });

  window.addEventListener('pagehide', stopCartFeedback);
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const motionChanged = (event) => { if (event.matches) { stopCartFeedback(); headerCount.skipMotion(); } };
  if (reducedMotion?.addEventListener) reducedMotion.addEventListener('change', motionChanged);
  else reducedMotion?.addListener?.(motionChanged);
  document.addEventListener('cart:refresh', () => initialize());
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
